import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_ENDPOINTS } from "../config/api";
import "../styles/RateMyChancesPage.css";
import BackToTop from "../components/BackToTop";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";

const RateMyChancesPage = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("general");
  const [testType, setTestType] = useState("GRE");
  const [testScore, setTestScore] = useState("");
  const [languageTest, setLanguageTest] = useState("TOEFL");
  const [languageScore, setLanguageScore] = useState("");
  const [gpa, setGpa] = useState("");
  const [country, setCountry] = useState("All");
  const [universityName, setUniversityName] = useState("");
  const [universitySuggestions, setUniversitySuggestions] = useState([]);
  const [results, setResults] = useState(null);
  const [admitProbability, setAdmitProbability] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  const fetchUniversitySuggestions = async (name) => {
    try {
      const response = await api.get(
        `${API_ENDPOINTS.RATE_MY_CHANCES.SUGGESTIONS}?query=${name}`
      );
      setUniversitySuggestions(response.data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  // Update university suggestions when user types
  useEffect(() => {
    if (mode === "specific" && universityName.length > 2) {
      fetchUniversitySuggestions(universityName);
    } else {
      setUniversitySuggestions([]);
    }
  }, [universityName, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Prepare the data, handling the case when no test is selected
    const formData = {
      testType: testType === "None" ? null : testType,
      testScore: testType === "None" ? null : testScore,
      languageTest,
      languageScore,
      gpa,
    };

    try {
      if (mode === "general") {
        const response = await api.post(API_ENDPOINTS.RATE_MY_CHANCES.GENERAL, {
          ...formData,
          country,
        });

        if (response.data.success) {
          setResults(response.data.results);
          setAdmitProbability(null);
        } else {
          setError(response.data.errors.join(", "));
        }
      } else {
        const response = await api.post(
          API_ENDPOINTS.RATE_MY_CHANCES.SPECIFIC,
          {
            ...formData,
            universityName,
          }
        );

        if (response.data.success) {
          setAdmitProbability(response.data.analysis);
          setResults(null);
        } else {
          setError(response.data.errors.join(", "));
        }
      }
    } catch (error) {
      console.error("Error calculating chances:", error);

      if (error.response?.status === 401) {
        navigate("/login");
      } else if (error.response?.data?.errors) {
        setError(error.response.data.errors.join(", "));
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError(
          "An error occurred while calculating your chances. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rate-my-chances-page">
        <div className="rate-my-chances-container">
          <div className="loading">Calculating your chances...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-my-chances-page">
      <SEO
        title="Rate My Chances - University Admission Calculator | Grad4Grad"
        description="Calculate your chances of getting admitted to top universities worldwide. Get personalized recommendations based on your GPA, test scores, and profile. Expert analysis for international students."
        keywords="university admission calculator, admission chances, university recommendations, GPA calculator, GRE GMAT scores, international student admission, study abroad chances"
        image="https://grad4grad.com/logo512.png"
        url="https://grad4grad.com/rate-my-chances"
        type="website"
      />
      <div className="rate-my-chances-container">
        <div className="rate-my-chances-header">
          <h1>Rate My Chances</h1>
          <p>
            Get personalized university recommendations based on your profile
          </p>
        </div>

        <div className="mode-selector">
          <button
            className={`mode-btn ${mode === "general" ? "active" : ""}`}
            onClick={() => setMode("general")}
          >
            General Recommendations
          </button>
          <button
            className={`mode-btn ${mode === "specific" ? "active" : ""}`}
            onClick={() => setMode("specific")}
          >
            Specific University
          </button>
        </div>

        <form onSubmit={handleSubmit} className="rate-my-chances-form">
          {mode === "general" && (
            <div className="input-group">
              <label>Country:</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="All">All Countries</option>
                <option value="USA">USA</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
          )}

          {mode === "specific" && (
            <div className="input-group university-search">
              <label>University Name:</label>
              <div className="search-input-container">
                <input
                  type="text"
                  className="search-input"
                  value={universityName}
                  onChange={(e) => setUniversityName(e.target.value)}
                  placeholder="Start typing university name..."
                />
                {universitySuggestions.length > 0 && (
                  <ul className="university-suggestions">
                    {universitySuggestions.map((uni, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setUniversityName(uni.name);
                          setUniversitySuggestions([]);
                        }}
                      >
                        <span className="suggestion-name">{uni.name}</span>
                        <span className="suggestion-acceptance">
                          {uni.country}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="input-group">
            <label>Test Type (GRE/GMAT):</label>
            <select
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
            >
              <option value="None">None (No GRE/GMAT)</option>
              <option value="GRE">GRE</option>
              <option value="GMAT">GMAT</option>
            </select>
          </div>

          {testType !== "None" && (
            <div className="input-group">
              <label>{testType} Score:</label>
              <input
                type="number"
                value={testScore}
                onChange={(e) => setTestScore(e.target.value)}
                placeholder={`Enter your ${testType} score`}
              />
            </div>
          )}

          <div className="input-group">
            <label>Language Test (TOEFL/IELTS):</label>
            <select
              value={languageTest}
              onChange={(e) => setLanguageTest(e.target.value)}
            >
              <option value="TOEFL">TOEFL</option>
              <option value="IELTS">IELTS</option>
            </select>
          </div>

          <div className="input-group">
            <label>{languageTest} Score:</label>
            <input
              type="number"
              value={languageScore}
              onChange={(e) => setLanguageScore(e.target.value)}
              placeholder={`Enter your ${languageTest} score`}
            />
          </div>

          <div className="input-group">
            <label>GPA:</label>
            <input
              type="number"
              step="0.1"
              value={gpa}
              onChange={(e) => setGpa(e.target.value)}
              placeholder="Enter your GPA"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-button">
            {mode === "general"
              ? "Calculate General Chance"
              : "Check Admission Chances"}
          </button>
        </form>

        {/* Results Section */}
        {mode === "general" && results && (
          <div className="results">
            <h3>Your University Recommendations</h3>
            <div className="results-container">
              <div className="result-category safe">
                <h4>Safe Universities</h4>
                <p className="category-description">
                  Universities where your profile strongly matches the
                  requirements
                </p>
                <ul className="university-list">
                  {results.safe.map((uni, index) => (
                    <li key={index}>
                      <span className="university-name">{uni.name}</span>
                      <span className="acceptance-rate">
                        {uni.acceptanceRate
                          ? `Acceptance Rate: ${uni.acceptanceRate}`
                          : ""}
                      </span>
                    </li>
                  ))}
                  {results.safe.length === 0 && (
                    <li>No safe universities found</li>
                  )}
                </ul>
              </div>

              <div className="result-category moderate">
                <h4>Moderate Universities</h4>
                <p className="category-description">
                  Universities where your profile is competitive but not
                  guaranteed
                </p>
                <ul className="university-list">
                  {results.moderate.map((uni, index) => (
                    <li key={index}>
                      <span className="university-name">{uni.name}</span>
                      <span className="acceptance-rate">
                        {uni.acceptanceRate
                          ? `Acceptance Rate: ${uni.acceptanceRate}`
                          : ""}
                      </span>
                    </li>
                  ))}
                  {results.moderate.length === 0 && (
                    <li>No moderate universities found</li>
                  )}
                </ul>
              </div>

              <div className="result-category ambitious">
                <h4>Ambitious Universities</h4>
                <p className="category-description">
                  Universities where your profile is below average but still
                  worth applying
                </p>
                <ul className="university-list">
                  {results.ambitious.map((uni, index) => (
                    <li key={index}>
                      <span className="university-name">{uni.name}</span>
                      <span className="acceptance-rate">
                        {uni.acceptanceRate
                          ? `Acceptance Rate: ${uni.acceptanceRate}`
                          : ""}
                      </span>
                    </li>
                  ))}
                  {results.ambitious.length === 0 && (
                    <li>No ambitious universities found</li>
                  )}
                </ul>
              </div>
            </div>
            <div className="results-note">
              <p>
                * These recommendations are based on your test scores, GPA, and
                university requirements. Other factors like research experience,
                work experience, and statement of purpose can significantly
                impact your chances. We recommend booking a free counseling
                session for a more detailed analysis.
              </p>
              <a
                href="https://shorturl.at/uvKtH"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button"
              >
                Book Free Counseling Session
              </a>
            </div>
          </div>
        )}

        {mode === "specific" && admitProbability && (
          <div className="results specific-results">
            <h3>Admission Analysis for {admitProbability.university}</h3>
            <div className="probability-meter">
              <div
                className="probability-bar"
                style={{ width: `${admitProbability.probability}%` }}
              >
                {admitProbability.probability.toFixed(1)}%
              </div>
            </div>
            <div
              className={`category-badge ${admitProbability.category.toLowerCase()}`}
            >
              {admitProbability.category}
            </div>

            <div className="analysis-details">
              <h4>Your Profile Analysis</h4>
              <div className="requirement-grid">
                <div className="requirement-item">
                  <h5>{admitProbability.requirements.test.type}</h5>
                  <p>Required: {admitProbability.requirements.test.required}</p>
                  <p>
                    Your Score: {admitProbability.requirements.test.yourScore}
                  </p>
                  <div className="score-indicator">
                    <div
                      className="score-bar"
                      style={{
                        width: `${
                          admitProbability.requirements.test.weight * 100
                        }%`,
                        backgroundColor:
                          admitProbability.requirements.test.weight >= 0.7
                            ? "#4CAF50"
                            : admitProbability.requirements.test.weight >= 0.4
                            ? "#FFC107"
                            : "#F44336",
                      }}
                    />
                  </div>
                </div>

                <div className="requirement-item">
                  <h5>GPA</h5>
                  <p>Required: {admitProbability.requirements.gpa.required}</p>
                  <p>Your GPA: {admitProbability.requirements.gpa.yourScore}</p>
                  <div className="score-indicator">
                    <div
                      className="score-bar"
                      style={{
                        width: `${
                          admitProbability.requirements.gpa.weight * 100
                        }%`,
                        backgroundColor:
                          admitProbability.requirements.gpa.weight >= 0.7
                            ? "#4CAF50"
                            : admitProbability.requirements.gpa.weight >= 0.4
                            ? "#FFC107"
                            : "#F44336",
                      }}
                    />
                  </div>
                </div>

                <div className="requirement-item">
                  <h5>{admitProbability.requirements.language.type}</h5>
                  <p>
                    Required: {admitProbability.requirements.language.required}
                  </p>
                  <p>
                    Your Score:{" "}
                    {admitProbability.requirements.language.yourScore}
                  </p>
                  <div className="score-indicator">
                    <div
                      className="score-bar"
                      style={{
                        width: `${
                          admitProbability.requirements.language.weight * 100
                        }%`,
                        backgroundColor:
                          admitProbability.requirements.language.weight >= 0.7
                            ? "#4CAF50"
                            : admitProbability.requirements.language.weight >=
                              0.4
                            ? "#FFC107"
                            : "#F44336",
                      }}
                    />
                  </div>
                </div>
              </div>

              {admitProbability.acceptanceRate && (
                <div className="acceptance-rate-info">
                  <p>
                    University Acceptance Rate:{" "}
                    {admitProbability.acceptanceRate}
                  </p>
                </div>
              )}
            </div>

            <div className="results-note">
              <p>
                * This analysis is based on your test scores, GPA, and
                university requirements. Other factors like research experience,
                work experience, and statement of purpose can significantly
                impact your chances. We recommend booking a free counseling
                session for a more detailed analysis.
              </p>
              <a
                href="https://shorturl.at/uvKtH"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button"
              >
                Book Free Counseling Session
              </a>
            </div>
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
};

export default RateMyChancesPage;
